import { useState, useEffect, useCallback, useRef } from "react";
import { interpolateMergeTags } from "./mergeTags";
import MergePreviewBar, { DUMMY_PREVIEW_CONTACT, DUMMY_PREVIEW_SENDER } from "./MergePreviewBar";

// ─── Subject Rotation Panel ─────────────────────────────────────────────────
//
// Drop-in UI for configuring the Subject Rotation Engine (see
// subjectRotationEngine.js / db.js's subjectRotationConfigSchema on the
// backend). Used from both MailMerge.js (ownerType="mailmerge") and
// SequenceBuilder.js (ownerType="sequence", one panel per step). Reads/writes
// exclusively through /api/subject-rotation* — it never touches rotation
// `cursor` state directly, that's engine-owned.
//
// Theme-agnostic: MailMerge.js has a `C` theme object, SequenceBuilder.js has
// `T` — both share the same key names (bg, border, text, textSub, textMuted,
// accent, green, red, amber, purple), so this component accepts either as
// the `theme` prop and falls back to sane defaults if omitted.

const DEFAULT_THEME = {
  bg: "#f8f9fb", white: "#fff", border: "#e2e6f0", borderLight: "#edebe9",
  text: "#1b1f2e", textSub: "#4a5078", textMuted: "#8890b0",
  accent: "#0078d4", accentL: "#deecf9",
  green: "#107c10", greenL: "#dff6dd",
  red: "#a4262c", redL: "#fde7e9",
  amber: "#d83b01", amberL: "#fed9cc",
  purple: "#5c2d91", purpleL: "#f4f0ff",
};

const MODES = [
  { id: "sequential", label: "Sequential", desc: "Use each subject in order for N sends, then advance." },
  { id: "random", label: "Random", desc: "Pick a random subject for every send." },
  { id: "weighted", label: "Weighted", desc: "Pick subjects by relative probability weight." },
];

// Multivariate rotation: the exact same atomic, concurrency-safe engine that
// powers Subject Line rotation (subjectRotationEngine.js) now runs per
// "dimension" — each dimension is its own SubjectRotationConfig document
// (same schema, keyed by tenantId+ownerType+ownerId+stepIndex+dimension), so
// Opening Line and Signature/CTA rotation get independent variant pools,
// independent CAS-protected cursors, and independent per-variant analytics
// for free, with zero new backend concurrency logic.
// "text" is a generic field name in the schema/API (kept for backward
// compatibility with existing Subject data) — DIMENSIONS below is what
// relabels it per-dimension in this UI.
//
// "From Name" is intentionally NOT a tab here even though it's a valid
// schema-level dimension (db.js): for mail sent through a connected
// Microsoft/Google mailbox (the common case), the recipient always sees
// that account's own configured display name — a per-send override only
// takes effect on the direct SMTP fallback path (see server.js's
// ROTATION_DIMENSIONS comment). A toggle that silently does nothing for
// most sends would be misleading, so it stays out until sender-name
// override is genuinely supported end-to-end.
//
// "Send Time" (isTimeWindow: true) is a different shape of variant than
// the other three — a time-of-day window, not text — so it gets its own
// editor (see the isTimeWindow branches below) instead of the free-text/
// merge-tag input. It's also sequence-only and shared across every step
// (see server.js's ROTATION_DIMENSIONS comment for why), so this panel
// must be mounted with stepIndex={null} for it — SequenceBuilder.js
// mounts one Send Time panel per sequence, not per step, unlike the other
// three dimensions.
export const DIMENSIONS = [
  { id: "subject", label: "Subject Line", itemLabel: "subject line", noun: "subject", multiline: false,
    placeholder: (i) => `Subject line ${i + 1} — try {{FirstName}}, {{Company}}...`,
    offLabel: "Off — using the single subject line above" },
  { id: "openingLine", label: "Opening Line", itemLabel: "opening line", noun: "opening line", multiline: true,
    placeholder: (i) => `Opening line ${i + 1} — try {{FirstName}}, {{Company}}...`,
    offLabel: "Off — using the email as written", hint: "Applied as the first line of the email body." },
  { id: "signature", label: "Signature / CTA", itemLabel: "signature/CTA", noun: "signature", multiline: true,
    placeholder: (i) => `Signature/CTA variant ${i + 1}`,
    offLabel: "Off — using the email as written", hint: "Appended at the end of the email body." },
  { id: "sendTime", label: "Send Time", itemLabel: "time window", noun: "time window", multiline: false, isTimeWindow: true,
    offLabel: "Off — sends go out as soon as your send window allows",
    hint: "Rotates which time-of-day window each step lands in (from the 2nd email of the sequence onward — the first touch always goes out right away) so you can see which time actually gets opened." },
];

// 0-24, half-open [start,end) — 24 reads as midnight/end-of-day, same
// convention the backend uses (db.js's startHour/endHour, 0-23 / 1-24).
function formatHourLabel(h) {
  const hh = ((Math.round(h) % 24) + 24) % 24;
  const period = hh < 12 ? "AM" : "PM";
  let display = hh % 12;
  if (display === 0) display = 12;
  return `${display}:00 ${period}`;
}
function windowLabel(startHour, endHour) {
  if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) return "";
  return `${formatHourLabel(startHour)} – ${formatHourLabel(endHour)}`;
}
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h); // 0-23, for start
const END_HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => h + 1); // 1-24, for end

// This panel has no real recipient list to draw a preview contact from —
// it's configuring rotation for subject/opening/signature variants in the
// abstract, before any send exists — so it uses the same shared dummy
// contact MailMerge.js's editor popup falls back on (see MergePreviewBar.js
// for why this used to be a locally-defined, differently-styled constant).
const PREVIEW_CONTACT = DUMMY_PREVIEW_CONTACT;
const PREVIEW_SENDER = DUMMY_PREVIEW_SENDER;

function uid() {
  return "tmp_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function authHeaders() {
  const token = (typeof localStorage !== "undefined" && localStorage.getItem("sdr_token")) || "";
  return { "Content-Type": "application/json", Authorization: "Bearer " + token };
}

function Toggle({ on, onClick, C, disabled }) {
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled}
      style={{
        width: 38, height: 20, borderRadius: 10, border: "none", padding: 2,
        background: on ? C.accent : C.border, cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", justifyContent: on ? "flex-end" : "flex-start", transition: "background .15s",
        opacity: disabled ? 0.6 : 1,
      }}>
      <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)", display: "block" }} />
    </button>
  );
}

function StatBar({ label, value, color, C }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
      <span style={{ width: 74, color: C.textSub, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: C.borderLight || C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, value)}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ width: 40, textAlign: "right", color: C.text, fontWeight: 600 }}>{value}%</span>
    </div>
  );
}

export default function SubjectRotationPanel({ ownerType, ownerId, stepIndex = null, dimension = "subject", theme, sampleBody = "" }) {
  const C = { ...DEFAULT_THEME, ...(theme || {}) };
  const dim = DIMENSIONS.find((d) => d.id === dimension) || DIMENSIONS[0];
  const [config, setConfig] = useState(null); // { enabled, mode, interval, subjects, cursor }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const dragIdx = useRef(null);

  const query = `ownerType=${encodeURIComponent(ownerType)}&ownerId=${encodeURIComponent(ownerId || "")}${stepIndex != null ? `&stepIndex=${stepIndex}` : ""}&dimension=${encodeURIComponent(dimension)}`;

  const load = useCallback(() => {
    if (!ownerId) return;
    setLoading(true);
    setError("");
    fetch(`/api/subject-rotation?${query}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (d && !d.error) {
          setConfig({
            enabled: !!d.enabled,
            mode: d.mode || "sequential",
            interval: d.interval || 1,
            subjects: (d.subjects || []).map((s) => ({ ...s, _key: String(s._id || s.id || uid()) })),
          });
          setDirty(false);
        } else {
          setError(d?.error || "Could not load rotation settings.");
        }
      })
      .catch(() => setError("Could not load rotation settings."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, ownerType, stepIndex, dimension]);

  useEffect(() => { load(); }, [load]);

  const loadAnalytics = useCallback(() => {
    if (!ownerId) return;
    setAnalyticsLoading(true);
    fetch(`/api/subject-rotation/analytics?${query}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setAnalytics(d))
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, ownerType, stepIndex, dimension]);

  useEffect(() => { if (showAnalytics) loadAnalytics(); }, [showAnalytics, loadAnalytics]);

  if (!ownerId) {
    return (
      <div style={{ padding: 16, background: C.bg, border: `1px dashed ${C.border}`, borderRadius: 8, color: C.textMuted, fontSize: 13, fontFamily: "'Segoe UI',sans-serif" }}>
        Save this {ownerType === "sequence" ? "sequence" : "mail merge"} first to configure {dim.itemLabel} rotation.
      </div>
    );
  }
  if (loading && !config) {
    return <div style={{ padding: 16, color: C.textMuted, fontSize: 13, fontFamily: "'Segoe UI',sans-serif" }}>Loading rotation settings…</div>;
  }
  if (!config) {
    return <div style={{ padding: 16, color: C.red, fontSize: 13, fontFamily: "'Segoe UI',sans-serif" }}>{error || "Unable to load."}</div>;
  }

  const mutate = (patch) => { setConfig((c) => ({ ...c, ...patch })); setDirty(true); };
  const mutateSubject = (key, patch) => {
    setConfig((c) => ({ ...c, subjects: c.subjects.map((s) => (s._key === key ? { ...s, ...patch } : s)) }));
    setDirty(true);
  };

  const addSubject = () => {
    let startHour = null, endHour = null, text = "";
    if (dim.isTimeWindow) {
      // Default to the 2-hour slot right after the last window already
      // configured (so adding windows one at a time naturally tiles the
      // day), or 8-10 AM as a first guess when there aren't any yet.
      const lastEnd = config.subjects.reduce((max, s) => Number.isFinite(s.endHour) ? Math.max(max, s.endHour) : max, null);
      startHour = lastEnd != null && lastEnd < 22 ? lastEnd : 8;
      endHour = startHour + 2;
      text = windowLabel(startHour, endHour);
    }
    const s = { _key: uid(), text, weight: 1, order: config.subjects.length, active: true, startHour, endHour, stats: { sent: 0, opened: 0, replied: 0, bounced: 0, converted: 0 } };
    setConfig((c) => ({ ...c, subjects: [...c.subjects, s] }));
    setDirty(true);
  };
  const mutateWindow = (key, patch) => {
    setConfig((c) => ({
      ...c,
      subjects: c.subjects.map((s) => {
        if (s._key !== key) return s;
        const next = { ...s, ...patch };
        return { ...next, text: windowLabel(next.startHour, next.endHour) };
      }),
    }));
    setDirty(true);
  };
  const duplicateSubject = (key) => {
    const orig = config.subjects.find((s) => s._key === key);
    if (!orig) return;
    const copy = { ...orig, _id: undefined, _key: uid(), text: orig.text + " (copy)" };
    setConfig((c) => {
      const idx = c.subjects.findIndex((s) => s._key === key);
      const next = c.subjects.slice();
      next.splice(idx + 1, 0, copy);
      return { ...c, subjects: next.map((s, i) => ({ ...s, order: i })) };
    });
    setDirty(true);
  };
  const deleteSubject = (key) => {
    setConfig((c) => ({ ...c, subjects: c.subjects.filter((s) => s._key !== key).map((s, i) => ({ ...s, order: i })) }));
    setDirty(true);
  };
  const moveSubject = (key, dir) => {
    setConfig((c) => {
      const idx = c.subjects.findIndex((s) => s._key === key);
      const swapWith = idx + dir;
      if (swapWith < 0 || swapWith >= c.subjects.length) return c;
      const next = c.subjects.slice();
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return { ...c, subjects: next.map((s, i) => ({ ...s, order: i })) };
    });
    setDirty(true);
  };
  const insertTag = (key, tag) => {
    const s = config.subjects.find((x) => x._key === key);
    if (!s) return;
    mutateSubject(key, { text: (s.text || "") + tag });
  };

  const save = async () => {
    if (dim.isTimeWindow) {
      // Same rules the backend enforces (validateSendTimeWindows in
      // server.js) — checked client-side too so a bad window shows up
      // immediately next to the field instead of only after a round trip.
      const windows = config.subjects.filter((s) => s.active !== false);
      const badRange = windows.find((s) => !Number.isFinite(s.startHour) || !Number.isFinite(s.endHour) || s.endHour <= s.startHour);
      if (config.enabled && badRange) { setError(`"${badRange.text}" needs a valid start time before its end time.`); return; }
      const sorted = windows.slice().sort((a, b) => a.startHour - b.startHour);
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].startHour < sorted[i - 1].endHour) {
          setError(`Time windows can't overlap ("${sorted[i - 1].text}" and "${sorted[i].text}").`);
          return;
        }
      }
    }
    setSaving(true);
    setError("");
    setSavedMsg("");
    try {
      const body = {
        ownerType, ownerId, stepIndex, dimension,
        enabled: config.enabled, mode: config.mode, interval: Math.max(1, parseInt(config.interval, 10) || 1),
        subjects: config.subjects.map((s, i) => ({
          _id: s._id, text: s.text, weight: s.weight, order: i, active: s.active !== false,
          ...(dim.isTimeWindow ? { startHour: s.startHour, endHour: s.endHour } : {}),
        })),
      };
      const r = await fetch("/api/subject-rotation", { method: "PUT", headers: authHeaders(), body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || "Save failed");
      setConfig({ enabled: !!d.enabled, mode: d.mode, interval: d.interval, subjects: (d.subjects || []).map((s) => ({ ...s, _key: String(s._id) })) });
      setDirty(false);
      setSavedMsg("Saved");
      setTimeout(() => setSavedMsg(""), 2000);
      if (showAnalytics) loadAnalytics();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetRotation = async () => {
    if (!window.confirm(`Restart rotation from the first ${dim.itemLabel}? Analytics are kept — only the current position resets.`)) return;
    try {
      await fetch("/api/subject-rotation/reset", { method: "POST", headers: authHeaders(), body: JSON.stringify({ ownerType, ownerId, stepIndex, dimension }) });
      load();
    } catch (e) { setError("Reset failed."); }
  };

  const activeSubjects = config.subjects.filter((s) => s.active !== false && (s.text || "").trim());
  const previewSubject = activeSubjects[previewIdx % Math.max(1, activeSubjects.length)];

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: "'Segoe UI',sans-serif", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: config.enabled ? `1px solid ${C.border}` : "none", background: C.bg }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Toggle on={config.enabled} onClick={() => mutate({ enabled: !config.enabled })} C={C} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{dim.label} Rotation</div>
            <div style={{ fontSize: 11, color: C.textMuted }}>
              {config.enabled ? `${activeSubjects.length} active ${dim.itemLabel}${activeSubjects.length === 1 ? "" : "s"} · ${MODES.find((m) => m.id === config.mode)?.label || config.mode}` : dim.offLabel}
            </div>
            {dim.hint && <div style={{ fontSize: 10.5, color: C.textMuted, marginTop: 1, fontStyle: "italic" }}>{dim.hint}</div>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {savedMsg && <span style={{ fontSize: 12, color: C.green }}>✓ {savedMsg}</span>}
          <button onClick={() => setShowAnalytics((v) => !v)} style={{ fontSize: 12, padding: "5px 10px", borderRadius: 4, border: `1px solid ${C.border}`, background: showAnalytics ? C.accentL : C.white, color: C.text, cursor: "pointer" }}>
            {showAnalytics ? "Hide analytics" : "View analytics"}
          </button>
          <button onClick={save} disabled={!dirty || saving} style={{ fontSize: 12, padding: "5px 12px", borderRadius: 4, border: "none", background: dirty ? C.accent : C.border, color: dirty ? "#fff" : C.textMuted, cursor: dirty && !saving ? "pointer" : "not-allowed", fontWeight: 600 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div style={{ padding: "8px 16px", background: C.redL, color: C.red, fontSize: 12 }}>{error}</div>}

      {config.enabled && (
        <div style={{ padding: 16 }}>
          {/* Mode selector */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {MODES.map((m) => (
              <button key={m.id} onClick={() => mutate({ mode: m.id })} title={m.desc}
                style={{
                  flex: 1, textAlign: "left", padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                  border: `1px solid ${config.mode === m.id ? C.accent : C.border}`,
                  background: config.mode === m.id ? C.accentL : C.white,
                }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: config.mode === m.id ? C.accent : C.text }}>{m.label}</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>{m.desc}</div>
              </button>
            ))}
          </div>

          {config.mode === "sequential" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 12, color: C.textSub }}>
              <span>Change {dim.noun} every</span>
              <input type="number" min={1} value={config.interval} onChange={(e) => mutate({ interval: e.target.value })}
                style={{ width: 54, padding: "4px 6px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, textAlign: "center" }} />
              <span>email{Number(config.interval) === 1 ? "" : "s"}, then advance and loop back to the start.</span>
              <button onClick={resetRotation} style={{ marginLeft: "auto", fontSize: 11, padding: "4px 8px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.white, color: C.textSub, cursor: "pointer" }}>
                Restart rotation
              </button>
            </div>
          )}

          {/* Subjects list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {config.subjects.map((s, i) => (
              <div key={s._key} draggable
                onDragStart={() => (dragIdx.current = i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx.current != null && dragIdx.current !== i) moveSubject(config.subjects[dragIdx.current]._key, i - dragIdx.current); dragIdx.current = null; }}
                style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: 8, border: `1px solid ${C.borderLight || C.border}`, borderRadius: 6, background: s.active === false ? C.bg : C.white }}>
                <span style={{ cursor: "grab", color: C.textMuted, fontSize: 13, padding: "6px 0" }}>⠿</span>
                <div style={{ flex: 1 }}>
                  {dim.isTimeWindow ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <select value={s.startHour ?? ""} onChange={(e) => mutateWindow(s._key, { startHour: Number(e.target.value) })}
                        style={{ padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12.5 }}>
                        {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                      </select>
                      <span style={{ color: C.textMuted, fontSize: 12 }}>to</span>
                      <select value={s.endHour ?? ""} onChange={(e) => mutateWindow(s._key, { endHour: Number(e.target.value) })}
                        style={{ padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12.5 }}>
                        {END_HOUR_OPTIONS.map((h) => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                      </select>
                      {Number.isFinite(s.startHour) && Number.isFinite(s.endHour) && s.endHour <= s.startHour && (
                        <span style={{ fontSize: 11, color: C.red }}>End must be after start</span>
                      )}
                    </div>
                  ) : dim.multiline ? (
                    <textarea value={s.text} onChange={(e) => mutateSubject(s._key, { text: e.target.value })} placeholder={dim.placeholder(i)} rows={2}
                      style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12.5, boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
                  ) : (
                    <input value={s.text} onChange={(e) => mutateSubject(s._key, { text: e.target.value })} placeholder={dim.placeholder(i)}
                      style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12.5, boxSizing: "border-box" }} />
                  )}
                  {!dim.isTimeWindow && (
                    <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                      {["{{FirstName}}", "{{Company}}", "{{Title}}"].map((tag) => (
                        <button key={tag} onClick={() => insertTag(s._key, tag)} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, border: `1px solid ${C.border}`, background: C.bg, color: C.textSub, cursor: "pointer" }}>{tag}</button>
                      ))}
                    </div>
                  )}
                </div>
                {config.mode === "weighted" && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <span style={{ fontSize: 9, color: C.textMuted }}>weight</span>
                    <input type="number" min={0} value={s.weight ?? 1} onChange={(e) => mutateSubject(s._key, { weight: Number(e.target.value) })}
                      style={{ width: 44, padding: "4px 6px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, textAlign: "center" }} />
                  </div>
                )}
                <div style={{ display: "flex", gap: 2, paddingTop: 4 }}>
                  <button onClick={() => moveSubject(s._key, -1)} disabled={i === 0} title="Move up" style={{ ...iconBtn(C), opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                  <button onClick={() => moveSubject(s._key, 1)} disabled={i === config.subjects.length - 1} title="Move down" style={{ ...iconBtn(C), opacity: i === config.subjects.length - 1 ? 0.3 : 1 }}>↓</button>
                  <button onClick={() => mutateSubject(s._key, { active: s.active === false })} title={s.active === false ? "Enable" : "Disable"} style={iconBtn(C)}>{s.active === false ? "⏸" : "✓"}</button>
                  <button onClick={() => duplicateSubject(s._key)} title="Duplicate" style={iconBtn(C)}>⧉</button>
                  <button onClick={() => deleteSubject(s._key)} title="Delete" style={{ ...iconBtn(C), color: C.red }}>✕</button>
                </div>
              </div>
            ))}
          </div>

          <button onClick={addSubject} style={{ marginTop: 8, fontSize: 12, padding: "6px 12px", borderRadius: 4, border: `1px dashed ${C.accent}`, background: C.white, color: C.accent, cursor: "pointer", fontWeight: 600 }}>
            + Add {dim.itemLabel}
          </button>

          {/* Preview */}
          {activeSubjects.length > 0 && !dim.isTimeWindow && (
            <div style={{ marginTop: 14, padding: 10, background: C.bg, borderRadius: 6 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 6 }}>
                <MergePreviewBar recipient={PREVIEW_CONTACT} dummy theme={C} compact/>
                <button onClick={() => setPreviewIdx((v) => v + 1)} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.white, color: C.textSub, cursor: "pointer" }}>
                  Next {dim.noun} ▸
                </button>
              </div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: dim.multiline ? 400 : 600, whiteSpace: dim.multiline ? "pre-wrap" : "normal" }}>
                {previewSubject ? interpolateMergeTags(previewSubject.text, PREVIEW_CONTACT, PREVIEW_SENDER) : ""}
              </div>
            </div>
          )}
          {/* Send Time gets a simple summary instead of a merge-tag preview
              — there's no recipient-specific content to render, just the
              set of windows that'll rotate. */}
          {activeSubjects.length > 0 && dim.isTimeWindow && (
            <div style={{ marginTop: 14, padding: 10, background: C.bg, borderRadius: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.textSub }}>ROTATING WINDOWS</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {activeSubjects.map((s) => (
                  <span key={s._key} style={{ fontSize: 12, padding: "3px 9px", borderRadius: 12, background: C.accentL, color: C.accent, fontWeight: 600 }}>
                    {windowLabel(s.startHour, s.endHour)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analytics */}
      {showAnalytics && (
        <div style={{ padding: 16, borderTop: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{dim.label} performance</span>
            <button onClick={loadAnalytics} style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.white, color: C.textSub, cursor: "pointer" }}>
              {analyticsLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          {!analytics || !analytics.subjects || analytics.subjects.length === 0 ? (
            <div style={{ fontSize: 12, color: C.textMuted }}>No sends recorded yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {analytics.subjects.map((s) => (
                <div key={s.id} style={{ padding: 10, border: `1px solid ${C.borderLight || C.border}`, borderRadius: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{s.text || "(empty)"}</span>
                    <span style={{ fontSize: 11, color: C.textMuted }}>{s.sent} sent</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <StatBar label="Open rate" value={s.openRate} color={C.accent} C={C} />
                    {/* Reply/bounce/conversion aren't attributed per-window (or
                        per opening-line/signature variant) anywhere in this
                        codebase yet — only sent + opened are. Showing those
                        bars here would just be a misleading, permanently-0%
                        row, so Send Time's card intentionally stops at Open
                        rate rather than implying data that doesn't exist. */}
                    {!dim.isTimeWindow && (
                      <>
                        <StatBar label="Reply rate" value={s.replyRate} color={C.green} C={C} />
                        <StatBar label="Bounce rate" value={s.bounceRate} color={C.red} C={C} />
                        <StatBar label="Conversion" value={s.conversionRate} color={C.purple} C={C} />
                      </>
                    )}
                  </div>
                </div>
              ))}
              {analytics.totals && (
                <div style={{ fontSize: 11, color: C.textMuted, textAlign: "right" }}>
                  {dim.isTimeWindow
                    ? `Overall: ${analytics.totals.sent} sent · ${analytics.totals.openRate}% open`
                    : `Overall: ${analytics.totals.sent} sent · ${analytics.totals.openRate}% open · ${analytics.totals.replyRate}% reply`}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function iconBtn(C) {
  return { width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, border: `1px solid ${C.border}`, borderRadius: 4, background: C.white, color: C.textSub, cursor: "pointer", padding: 0 };
}
